import { UsageType } from '../constants/usageType';
import { UserTagGroupItemWithGroup } from './merchant-applications';
import { throwOnError } from './utils';

export type ShopApplicationStatus =
  | 'submitted'
  | 'reviewing'
  | 'pending'
  | 'approved'
  | 'rejected';
export type ShopApplicationKind = 'new' | 'update' | 'disable';

export type ShopApplication = {
  id: string;
  organization_code: string | null;
  organization_name: string | null;
  shop_name: string;
  shop_name_furigana: string;
  postal_code: string;
  address: string;
  tel: string;
  email: string | null;
  contact_person_name: string;
  contact_person_name_furigana: string;
  private_money_ids: string[];
  user_tag_group_items: UserTagGroupItemWithGroup[];
  // 店舗形態（per-shop）。未設定は null。
  usage_type: UsageType | null;
  status: ShopApplicationStatus;
  held_at: string | null;
  held_by_admin_user_id: string | null;
  applied_at: string | null;
  approved_at: string | null;
  approved_by_admin_user_id: string | null;
  shop_id: string | null;
  kind: ShopApplicationKind;
  target_shop_id: string | null;
  last_updated_by_admin_user: {
    id: string;
    name: string;
  } | null;
  created_at: string;
  updated_at: string;
};

export type ShopApplicationListResponse = {
  pagination: {
    has_next: boolean;
    has_prev: boolean;
    max_page: number;
    per_page: number;
    current: number;
  };
  count: number;
  rows: ShopApplication[];
};

export type CreateShopApplicationRequest = {
  organization_code?: string;
  shop_name: string;
  shop_name_furigana: string;
  postal_code: string;
  address: string;
  tel: string;
  email?: string;
  contact_person_name: string;
  contact_person_name_furigana: string;
  private_money_ids: string[];
  user_tag_group_item_ids?: string[];
  target_shop_id?: string;
  // 店舗形態（フロントで必須）。
  usage_type?: UsageType;
};

export type UpdateShopApplicationRequest = {
  shop_name?: string;
  shop_name_furigana?: string;
  postal_code?: string;
  address?: string;
  tel?: string;
  email?: string;
  contact_person_name?: string;
  contact_person_name_furigana?: string;
  private_money_ids?: string[];
  user_tag_group_item_ids?: string[];
  // 店舗形態。未編集で据え置く場合のみ null（空文字は 422）。
  usage_type?: UsageType | null;
};

export const createShopApplication = (
  postData: CreateShopApplicationRequest
): Promise<ShopApplication> =>
  fetch('/api/shop-applications', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then((response) => response.json());

export const fetchShopApplications = (query = ''): Promise<ShopApplicationListResponse> =>
  fetch(`/api/shop-applications?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError);

type FetchCSVResponse = { url: string };

export const fetchShopApplicationsCSV = (query = ''): Promise<FetchCSVResponse> =>
  fetch(`/api/shop-applications/csv?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<FetchCSVResponse>);

export const showShopApplication = (id: string): Promise<ShopApplication> =>
  fetch(`/api/shop-applications/${id}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError);

export const approveShopApplication = (id: string): Promise<ShopApplication> =>
  fetch(`/api/shop-applications/${id}/approve`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then((response) => response.json());

export const rejectShopApplication = (id: string): Promise<ShopApplication> =>
  fetch(`/api/shop-applications/${id}/reject`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then((response) => response.json());

// submitted / pending の申請を審査中(reviewing)に遷移させる
export const reviewShopApplication = (id: string): Promise<ShopApplication> =>
  fetch(`/api/shop-applications/${id}/review`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then((response) => response.json());

// submitted / reviewing の申請を保留(pending)に遷移させる
export const holdShopApplication = (id: string): Promise<ShopApplication> =>
  fetch(`/api/shop-applications/${id}/hold`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then((response) => response.json());

// pending の申請を保留解除(submitted に復帰)する
export const unholdShopApplication = (id: string): Promise<ShopApplication> =>
  fetch(`/api/shop-applications/${id}/unhold`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then((response) => response.json());

export const updateShopApplication = (
  id: string,
  data: UpdateShopApplicationRequest
): Promise<ShopApplication> =>
  fetch(`/api/shop-applications/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...data }),
  }).then((response) => response.json());
